import React from 'react';
import '../stylings/BucksAdminOrderDetails.css';

const BucksAdminOrderDetails = ({ 
  selectedOrder, 
  isOpen, 
  onClose, 
  onUpdate, 
  formatDate, 
  formatCurrency, 
  getStatusBadgeClass,
  ref
}) => {
  // Handle print function
  const handlePrint = () => {
    window.print();
  };

  // Handle backdrop click to close panel
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Touch gesture handling for mobile
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    // Close on right swipe (swipe away)
    if (isRightSwipe && window.innerWidth <= 767) {
      onClose();
    }
  };

  // Handle escape key to close panel
  React.useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!selectedOrder) {
    return null;
  }

  return (
    <>
      {/* Backdrop overlay for mobile/tablet */}
      {isOpen && (
        <div 
          className="bucks-admin-order-details-backdrop" 
          onClick={handleBackdropClick}
        />
      )}
      
      <div 
        className={`bucks-admin-order-details-panel ${isOpen ? 'open' : ''}`} 
        ref={ref}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Mobile-specific floating close button */}
        <button 
          className="bucks-admin-order-details-mobile-close" 
          onClick={onClose}
          aria-label="Close order details"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Mobile pull indicator */}
        <div className="bucks-admin-order-details-pull-indicator">
          <div className="bucks-admin-order-details-pull-bar"></div>
          <span className="bucks-admin-order-details-swipe-hint">Swipe right to close</span>
        </div>

        <div className="bucks-admin-order-details-header">
          <div className="bucks-admin-order-details-title">
            <h2>Order Details</h2>
            <div className="bucks-admin-order-details-id">{selectedOrder.id}</div>
          </div>
          {/* Desktop close button */}
          <button 
            className="bucks-admin-order-details-close bucks-admin-order-details-close-desktop" 
            onClick={onClose}
            aria-label="Close details panel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="bucks-admin-order-details-content">
          {/* Status Information Section */}
          <div className="bucks-admin-order-details-section">
            <div className="bucks-admin-order-details-section-header">
              <h3>Status Information</h3>
            </div>
            <div className="bucks-admin-order-details-section-content">
              <div className="bucks-admin-order-details-info-row">
                <span className="bucks-admin-order-details-label">Status:</span>
                <span className={`bucks-admin-orders-status-badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                  {selectedOrder.status}
                </span>
              </div>
              <div className="bucks-admin-order-details-info-row">
                <span className="bucks-admin-order-details-label">Order Date:</span>
                <span className="bucks-admin-order-details-value">{formatDate(selectedOrder.created_at)}</span>
              </div>
              <div className="bucks-admin-order-details-info-row">
                <span className="bucks-admin-order-details-label">Last Updated:</span>
                <span className="bucks-admin-order-details-value">{formatDate(selectedOrder.updated_at)}</span>
              </div>
              {selectedOrder.tracking_number && (
                <div className="bucks-admin-order-details-info-row">
                  <span className="bucks-admin-order-details-label">Tracking Number:</span>
                  <span className="bucks-admin-order-details-value bucks-admin-order-details-tracking">
                    {selectedOrder.tracking_number}
                  </span>
                </div>
              )}
              {selectedOrder.admin_notes && (
                <div className="bucks-admin-order-details-info-row bucks-admin-order-details-notes-row">
                  <span className="bucks-admin-order-details-label">Admin Notes:</span>
                  <span className="bucks-admin-order-details-value bucks-admin-order-details-notes">
                    {selectedOrder.admin_notes}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Customer Information Section */}
          <div className="bucks-admin-order-details-section">
            <div className="bucks-admin-order-details-section-header">
              <h3>Customer Information</h3>
            </div>
            <div className="bucks-admin-order-details-section-content">
              <div className="bucks-admin-order-details-info-row">
                <span className="bucks-admin-order-details-label">Name:</span>
                <span className="bucks-admin-order-details-value">{selectedOrder.customer?.name || 'N/A'}</span>
              </div>
              <div className="bucks-admin-order-details-info-row">
                <span className="bucks-admin-order-details-label">Email:</span>
                <span className="bucks-admin-order-details-value">
                  {selectedOrder.customer?.email ? (
                    <a href={`mailto:${selectedOrder.customer.email}`} className="bucks-admin-order-details-link">
                      {selectedOrder.customer.email}
                    </a>
                  ) : 'N/A'}
                </span>
              </div>
              <div className="bucks-admin-order-details-info-row">
                <span className="bucks-admin-order-details-label">Phone:</span>
                <span className="bucks-admin-order-details-value">
                  {selectedOrder.customer?.phone ? (
                    <a href={`tel:${selectedOrder.customer.phone}`} className="bucks-admin-order-details-link">
                      {selectedOrder.customer.phone}
                    </a>
                  ) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Shipping Address Section */}
          <div className="bucks-admin-order-details-section">
            <div className="bucks-admin-order-details-section-header">
              <h3>Shipping Address</h3>
            </div>
            <div className="bucks-admin-order-details-section-content">
              <div className="bucks-admin-order-details-address">
                <p>{selectedOrder.shipping_address?.street || 'No street address'}</p>
                <p>
                  {selectedOrder.shipping_address?.city || 'No city'}, {selectedOrder.shipping_address?.state || 'No state'} {selectedOrder.shipping_address?.zipcode || 'No zip'}
                </p>
                {selectedOrder.shipping_address?.country && (
                  <p>{selectedOrder.shipping_address.country}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Order Items Section */}
          <div className="bucks-admin-order-details-section">
            <div className="bucks-admin-order-details-section-header">
              <h3>Order Items ({selectedOrder.items?.length || 0} items)</h3>
            </div>
            <div className="bucks-admin-order-details-section-content">
              <div className="bucks-admin-order-details-items">
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  selectedOrder.items.map((item, index) => (
                    <div key={item.id || index} className="bucks-admin-order-details-item">
                      <div className="bucks-admin-order-details-item-info">
                        <div className="bucks-admin-order-details-item-name">{item.name || 'Unknown Product'}</div>
                        <div className="bucks-admin-order-details-item-details">
                          <span className="bucks-admin-order-details-item-price">
                            {formatCurrency(item.price || 0)} × {item.quantity || 1}
                          </span>
                          {item.variant && (
                            <span className="bucks-admin-order-details-item-variant">
                              Variant: {item.variant}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="bucks-admin-order-details-item-total">
                        {formatCurrency(item.total || (item.price * item.quantity) || 0)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bucks-admin-order-details-no-items">
                    No items found for this order
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Payment Information Section */}
          <div className="bucks-admin-order-details-section">
            <div className="bucks-admin-order-details-section-header">
              <h3>Payment Information</h3>
            </div>
            <div className="bucks-admin-order-details-section-content">
              <div className="bucks-admin-order-details-payment">
                <div className="bucks-admin-order-details-info-row">
                  <span className="bucks-admin-order-details-label">Payment Method:</span>
                  <span className="bucks-admin-order-details-value">{selectedOrder.payment_method || 'N/A'}</span>
                </div>
                
                {selectedOrder.payment_status && (
                  <div className="bucks-admin-order-details-info-row">
                    <span className="bucks-admin-order-details-label">Payment Status:</span>
                    <span className="bucks-admin-order-details-value">{selectedOrder.payment_status}</span>
                  </div>
                )}
                
                {selectedOrder.transaction_id && (
                  <div className="bucks-admin-order-details-info-row">
                    <span className="bucks-admin-order-details-label">Transaction ID:</span>
                    <span className="bucks-admin-order-details-value bucks-admin-order-details-transaction">
                      {selectedOrder.transaction_id}
                    </span>
                  </div>
                )}
                
                <div className="bucks-admin-order-details-totals">
                  <div className="bucks-admin-order-details-total-row">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedOrder.subtotal || 0)}</span>
                  </div>
                  <div className="bucks-admin-order-details-total-row">
                    <span>Tax:</span>
                    <span>{formatCurrency(selectedOrder.tax || 0)}</span>
                  </div>
                  <div className="bucks-admin-order-details-total-row">
                    <span>Shipping:</span>
                    <span>{formatCurrency(selectedOrder.shipping || 0)}</span>
                  </div>
                  {selectedOrder.discount && selectedOrder.discount > 0 && (
                    <div className="bucks-admin-order-details-total-row bucks-admin-order-details-discount">
                      <span>Discount:</span>
                      <span>-{formatCurrency(selectedOrder.discount)}</span>
                    </div>
                  )}
                  <div className="bucks-admin-order-details-total-row bucks-admin-order-grand-total">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedOrder.total || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="bucks-admin-order-details-actions">
          <button 
            className="bucks-admin-order-details-update-btn" 
            onClick={() => onUpdate(null, selectedOrder)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Update Order
          </button>
          <button 
            className="bucks-admin-order-details-print-btn" 
            onClick={handlePrint}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"></polyline>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
              <rect x="6" y="14" width="12" height="8"></rect>
            </svg>
            Print Order
          </button>
        </div>
      </div>
    </>
  );
};

export default BucksAdminOrderDetails;